import React, { useState } from "react";
import { apiService } from "../services/api";
import { ImportWizard } from "./ImportWizard";
import { isXlsxFilename } from "../utils/datasetDownload";

interface UploadSuccessData {
  datasetId: number;
  projectId: number;
  filename: string;
  // The file as originally uploaded (before any display-name override) —
  // used only to tell whether this dataset was stored as an Excel workbook.
  originalFilename: string;
  rowCount: number;
}

interface SelfUploadModalProps {
  projectId: number;
  currentUserId: number;
  onSuccess: () => void;
  onClose: () => void;
}

export const SelfUploadModal: React.FC<SelfUploadModalProps> = ({
  projectId,
  currentUserId,
  onSuccess,
  onClose,
}) => {
  const [successData, setSuccessData] = useState<UploadSuccessData | null>(
    null,
  );
  const [downloading, setDownloading] = useState(false);

  const isWorkbook = successData
    ? isXlsxFilename(successData.originalFilename)
    : false;

  const handleDownload = async (format: "native" | "csv" = "native") => {
    if (!successData) return;
    setDownloading(true);
    try {
      const blob = await apiService.downloadDatasetPseudonymized(
        successData.datasetId,
        successData.projectId,
        undefined,
        format,
      );
      const ext = isWorkbook && format === "native" ? "xlsx" : "csv";
      const base = successData.filename.replace(/\.[^.]+$/, "");
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${base}.${ext}`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (err) {
      console.error("Download failed:", err);
    } finally {
      setDownloading(false);
    }
  };

  if (successData) {
    return (
      <div
        style={{
          position: "fixed",
          inset: 0,
          backgroundColor: "rgba(0,0,0,0.5)",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          zIndex: 1000,
          padding: "16px",
        }}
        onClick={(e) => {
          if (e.target === e.currentTarget) onClose();
        }}
      >
        <div
          style={{
            backgroundColor: "#f8f9fa",
            borderRadius: "8px",
            width: "100%",
            maxWidth: "560px",
            boxShadow: "0 8px 32px rgba(0,0,0,0.18)",
          }}
        >
          <div
            style={{
              padding: "20px",
              backgroundColor: "white",
              borderRadius: "8px 8px 0 0",
              display: "flex",
              justifyContent: "space-between",
              alignItems: "flex-start",
              borderBottom: "1px solid #dee2e6",
            }}
          >
            <div>
              <div
                style={{ fontWeight: 700, fontSize: "16px", color: "#1a1d23" }}
              >
                ✅ Upload Complete
              </div>
              <div
                style={{ fontSize: "12px", color: "#6c757d", marginTop: "2px" }}
              >
                Your file has been de-identified and is ready to download
              </div>
            </div>
            <button
              onClick={onClose}
              style={{
                background: "none",
                border: "none",
                fontSize: "20px",
                cursor: "pointer",
                color: "#6c757d",
                lineHeight: 1,
                padding: "0 4px",
              }}
            >
              ×
            </button>
          </div>
          <div style={{ padding: "20px" }}>
            <div
              style={{
                backgroundColor: "#f0fdf4",
                border: "1px solid #86efac",
                borderRadius: "6px",
                padding: "14px",
                marginBottom: "16px",
              }}
            >
              <div
                style={{
                  fontSize: "13px",
                  color: "#166534",
                  lineHeight: "1.5",
                }}
              >
                <div style={{ fontWeight: 600, marginBottom: "8px" }}>
                  {successData.filename}
                </div>
                <div style={{ fontSize: "12px" }}>
                  ✓ Processed with de-identification
                  <br />✓ {successData.rowCount.toLocaleString()} rows
                  <br />✓ Ready to download or share
                </div>
              </div>
            </div>
            <div style={{ display: "flex", gap: "8px" }}>
              <button
                onClick={() => handleDownload()}
                disabled={downloading}
                style={{
                  flex: 1,
                  padding: "12px 16px",
                  backgroundColor: downloading ? "#0d7c7c" : "#0d7c7c",
                  color: "white",
                  border: "none",
                  borderRadius: "6px",
                  fontWeight: 600,
                  fontSize: "14px",
                  cursor: downloading ? "not-allowed" : "pointer",
                  opacity: downloading ? 0.7 : 1,
                }}
              >
                {downloading
                  ? "Downloading..."
                  : isWorkbook
                    ? "⬇ Download De-identified Workbook"
                    : "⬇ Download De-identified File"}
              </button>
              {isWorkbook && (
                <button
                  onClick={() => handleDownload("csv")}
                  disabled={downloading}
                  style={{
                    padding: "12px 16px",
                    backgroundColor: "transparent",
                    color: "#0d7c7c",
                    border: "1px solid #0d7c7c",
                    borderRadius: "6px",
                    fontWeight: 600,
                    fontSize: "14px",
                    cursor: downloading ? "not-allowed" : "pointer",
                    opacity: downloading ? 0.7 : 1,
                  }}
                  title="Flattened first sheet, supports selecting a column subset"
                >
                  as CSV
                </button>
              )}
              <button
                onClick={onClose}
                style={{
                  padding: "12px 16px",
                  backgroundColor: "white",
                  color: "#495057",
                  border: "1px solid #dee2e6",
                  borderRadius: "6px",
                  fontWeight: 600,
                  fontSize: "14px",
                  cursor: "pointer",
                }}
              >
                Done
              </button>
            </div>
            <div
              style={{
                fontSize: "12px",
                color: "#6c757d",
                marginTop: "12px",
                textAlign: "center",
              }}
            >
              You can also find this file in your{" "}
              <a
                href="/datasets"
                style={{
                  color: "#0d7c7c",
                  textDecoration: "underline",
                  cursor: "pointer",
                }}
              >
                data catalog
              </a>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div
      style={{
        position: "fixed",
        inset: 0,
        backgroundColor: "rgba(0,0,0,0.5)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        zIndex: 1000,
        padding: "16px",
      }}
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div
        style={{
          backgroundColor: "#f8f9fa",
          borderRadius: "8px",
          width: "100%",
          maxWidth: "560px",
          maxHeight: "90vh",
          overflowY: "auto",
          boxShadow: "0 8px 32px rgba(0,0,0,0.18)",
        }}
      >
        {/* Header */}
        <div
          style={{
            padding: "16px 20px",
            borderBottom: "1px solid #dee2e6",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            backgroundColor: "white",
            borderRadius: "8px 8px 0 0",
          }}
        >
          <div>
            <div
              style={{ fontWeight: 700, fontSize: "16px", color: "#1a1d23" }}
            >
              Upload My Data
            </div>
            <div
              style={{ fontSize: "12px", color: "#6c757d", marginTop: "2px" }}
            >
              Your file will be de-identified and stored securely. You choose
              who can access it.
            </div>
          </div>
          <button
            onClick={onClose}
            style={{
              background: "none",
              border: "none",
              fontSize: "20px",
              cursor: "pointer",
              color: "#6c757d",
              lineHeight: 1,
              padding: "0 4px",
            }}
          >
            ×
          </button>
        </div>

        <div style={{ padding: "16px 20px" }}>
          <ImportWizard
            projectId={projectId}
            currentUserId={currentUserId}
            defaultDownloadScope="selected_members"
            uploadType="self"
            inspectRoute={`/projects/${projectId}/upload/inspect`}
            onUpload={async ({
              file,
              columns,
              displayName,
              metadataScope,
              downloadScope,
              downloadUserIds,
              notes,
              parseOptions,
              piiFindingsAcknowledged,
              piiValueRedactions,
              piiDroppedColumns,
            }) => {
              const result = await apiService.selfUploadProjectFile(
                projectId,
                file,
                columns,
                metadataScope,
                downloadScope,
                downloadUserIds,
                notes || undefined,
                displayName || undefined,
                piiFindingsAcknowledged ?? false,
                piiValueRedactions ?? [],
                piiDroppedColumns ?? [],
                parseOptions,
              );
              setSuccessData({
                datasetId: result.dataset_id,
                projectId: result.project_id,
                filename: displayName || file.name,
                originalFilename: file.name,
                rowCount: result.row_count,
              });
            }}
            onCancel={onClose}
          />
        </div>
      </div>
    </div>
  );
};
